import { SeriesLineOptions } from "highcharts";

import { toLocalTimeFromUTCWithOffset } from "datetime";

export interface HighchartDataBySeries {
  [seriesName: string]: {
    dataPoints: number[][];
    yAxisIndex: number;
    hideByDefault?: boolean;
  };
}
export interface HighchartPlotline {
  timestamp: number;
  text: string;
}
export interface HighchartYAxis {
  name: string;
  isPercentage: boolean;
}
export const getHighchartOptions = (
  {
    title,
    dataBySeries,
    plotLines = [],
    yAxis = [],
  }: {
    title?: string;
    dataBySeries: HighchartDataBySeries;
    plotLines?: HighchartPlotline[];
    yAxis: HighchartYAxis[];
  },
  onComplete?: VoidFunction
): Highcharts.Options => ({
  chart: {
    height: 500,
    zoomType: "xy",
    displayErrors: true,
    events: {
      load: onComplete,
    },
  },
  credits: {
    enabled: false,
  },
  plotOptions: {
    line: {
      marker: {
        enabled: false,
      },
    },
  },
  series: Object.keys(dataBySeries)
    .sort()
    .filter((seriesName) => dataBySeries[seriesName].dataPoints.length > 0)
    .map((seriesName) => {
      const dataForSeries = dataBySeries[seriesName];
      return {
        // force performance boost, but disable if 0 data as otherwise triggers a highcharts warning
        boostThreshold: dataForSeries.dataPoints.length > 0 ? 1 : 0,
        type: "line",
        yAxis: dataForSeries.yAxisIndex,
        name: seriesName,
        gapSize: 5,
        gapUnit: "relative",
        visible: !dataForSeries.hideByDefault,
        data: dataForSeries.dataPoints,
      } as SeriesLineOptions;
    }),
  xAxis: {
    crosshair: {
      width: 1,
      color: "red",
    },
    plotLines: (plotLines || []).map((plotLine) => ({
      color: "#FF0000",
      width: 2,
      value: plotLine.timestamp,
      label: {
        text: "",
      },
      events: {
        mouseover: function (e) {
          this.label.element.innerHTML = plotLine.text;
        },
        mouseout: function (e) {
          this.label.element.innerHTML = "";
        },
      },
    })),
    type: "datetime",
  },
  lang: {
    noData: "There is no data available to display",
  },
  tooltip: {
    split: true,
    formatter: function () {
      // The first returned item is the header, subsequent items are the
      // points
      const date = toLocalTimeFromUTCWithOffset(this.x);
      return [date].concat(
        this.points
          ? this.points.map(function (point) {
              return point.series.name + ": <b>" + point.y.toFixed(2) + "</b>";
            })
          : []
      );
    },
  },
  yAxis: yAxis.map((x, index) => ({
    title: {
      text: x.name,
    },
    min: null,
    max: null,
    opposite: index % 2 === 0,
    softMin: 0,
    softMax: x.isPercentage ? 1 : 0,
  })),
  boost: {
    enabled: true,
  },
  title: title
    ? {
        text: title,
        align: "center",
        verticalAlign: "top",
        margin: 20,
      }
    : undefined,
  exporting: {
    sourceWidth: 1080,
    sourceHeight: 720,
    // scale: 2 (default)
    chartOptions: {},
    buttons: {
      contextButton: {
        menuItems: [
          /*"printChart", "downloadPNG", "downloadSVG"*/
        ],
      },
    },
  },
  time: {
    useUTC: false,
  },
});
